// Regimes we surface the "Your Privacy Choices" footer link for. `CPRA` =
// California; `united-states-hidden` = rest of US (its Experience is hidden in
// the shared bundle, so US users get the footer link rather than an auto-banner).
const OPT_OUT_REGIMES = ['CPRA', 'cpra', 'united-states-hidden']

const CONSENT_MANAGER_POLL_MS = 100
const CONSENT_MANAGER_MAX_ATTEMPTS = 50 // ~5s

function whenConsentManagerReady(callback, attemptsLeft = CONSENT_MANAGER_MAX_ATTEMPTS) {
  if (typeof window.transcend?.showConsentManager === 'function') {
    callback(window.transcend)
  } else if (attemptsLeft > 0) {
    window.setTimeout(
      () => whenConsentManagerReady(callback, attemptsLeft - 1),
      CONSENT_MANAGER_POLL_MS,
    )
  }
}

// Opens the "Do Not Sell/Share" consent UI. Safe to call before the manager
// has finished loading — it polls briefly for window.transcend.
export function openConsentManager() {
  whenConsentManagerReady(transcend =>
    transcend.showConsentManager({ viewState: 'DoNotSellExplainer' }),
  )
}

// True when airgap has loaded and the visitor is in a regime we surface the
// opt-out UI for. Drives whether the "Your Privacy Choices" footer link renders.
export function isConsentManagementAvailable() {
  const regimes = window.airgap?.getRegimes?.()
  return !!regimes && OPT_OUT_REGIMES.some(regime => regimes.has(regime))
}

// Runs the callback once airgap.js has loaded (or is already loaded). No-op when
// airgap isn't present (REACT_APP_TRANSCEND_ENV empty/unset).
export function onAirgapReady(callback) {
  const airgap = window.airgap
  if (airgap && typeof airgap.ready === 'function') {
    airgap.ready(callback)
  }
}

// Resolves once airgap.js has finished loading (or immediately if it's not
// present, or after `timeoutMs` if it never readies). Lets callers read
// getConsent() reliably instead of racing airgap's async init — important right
// after login/signup, when airgap may still be initializing.
export function whenAirgapReady(timeoutMs = 5000) {
  return new Promise(resolve => {
    const airgap = window.airgap
    if (!airgap || typeof airgap.ready !== 'function') {
      resolve()
      return
    }
    let settled = false
    const finish = () => {
      if (!settled) {
        settled = true
        resolve()
      }
    }
    airgap.ready(finish)
    window.setTimeout(finish, timeoutMs)
  })
}

// Subscribes to airgap's `consent-change` event, which fires when the user
// updates their choice (e.g. via the consent manager / footer link). Returns an
// unsubscribe function. No-op when airgap isn't loaded.
export function onConsentChange(callback) {
  let listener
  onAirgapReady(() => {
    listener = () => callback()
    window.airgap.addEventListener('consent-change', listener)
  })
  return () => {
    if (listener) {
      window.airgap?.removeEventListener?.('consent-change', listener)
    }
  }
}

/**
 * Reads the visitor's current consent from airgap and shapes it for the
 * accounts-side consent endpoint (PUT /v1/uaa/consent/{userId}).
 *
 * Returns null only when airgap isn't loaded. Otherwise returns the current
 * state — including an unconfirmed default (`confirmed: false`) so a default
 * record can be created for users who never interacted. The BE guards against a
 * default overwriting an existing confirmed choice.
 *
 * Mapping note: airgap purposes are permission-style (SaleOfInfo === true means
 * "allowed to sell"), while the API's `saleOfInfo` means "opted OUT of sale" —
 * so we invert.
 */
export function getConsentSyncBody() {
  const consent = window.airgap?.getConsent?.()
  if (!consent) {
    return null
  }

  return {
    saleOfInfo: consent.purposes?.SaleOfInfo === false,
    confirmed: !!consent.confirmed,
    // API field is a LocalDateTime (no zone), so send UTC without the trailing
    // "Z"/millis: "2026-07-08T18:00:46".
    consentedAt: consent.timestamp
      ? new Date(consent.timestamp).toISOString().slice(0, 19)
      : undefined,
  }
}
